import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { StoreProvider, AuthProvider, LocationProvider, BrandProvider } from '@/app/providers'
import { AppRoutes } from '@/routes'
import { ErrorBoundary } from '@/components/common'
import { DebugLogProvider, DevDock } from '@/components/dev'
import { OfflineBanner, InstallPrompt, UpdatePrompt } from '@/pwa'

// Query Client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function App() {
  return (
    <ErrorBoundary>
      <StoreProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <DebugLogProvider>
              <LocationProvider>
                <BrandProvider>
                <BrowserRouter>
                  <AppRoutes />
              {/* Toaster sits at the app root. Our `notify` module
                  renders every variant via `toast.custom` so the
                  global toastOptions below mainly tune the container
                  spacing + the fallback styling for any third-party
                  calls to `toast()` that don't go through `notify`. */}
              <Toaster
                position="top-right"
                gutter={10}
                containerClassName="!top-4 !right-4"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: 'white',
                        color: '#111827',
                        borderRadius: '12px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)',
                        padding: '12px 16px',
                        fontSize: '14px',
                    },
                    success: { iconTheme: { primary: '#10b981', secondary: '#ecfdf5' } },
                    error:   { iconTheme: { primary: '#ef4444', secondary: '#fef2f2' }, duration: 6000 },
                }}
              />
              {/* PWA — offline indicator, install prompt and update toast.
                  All three render `null` unless their condition fires, so
                  mounting them at the root costs nothing in steady state. */}
              <OfflineBanner />
              <InstallPrompt />
              <UpdatePrompt />
              {/* Developer Dock — drawer + floating launcher. Renders
                  null for everyone except admin / site_admin / devops
                  so this mount has zero footprint on visitor sessions. */}
              <DevDock />
                </BrowserRouter>
                </BrandProvider>
              </LocationProvider>
            </DebugLogProvider>
          </AuthProvider>
        </QueryClientProvider>
      </StoreProvider>
    </ErrorBoundary>
  )
}

export default App
